import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiOrganization, type ApiUser } from '@/lib/api-client';

type MeResponse = {
  user: ApiUser;
  active_org_id: string;
  impersonator: { admin_id: string; admin_email: string; started_at: number } | null;
  organizations: ApiOrganization[];
};

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/app/me'),
    retry: false,
    staleTime: 30_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<{ user: ApiUser; organization: ApiOrganization }>('/api/auth/login', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      password: string;
      full_name?: string;
      organization_name?: string;
    }) => api.post<{ user: ApiUser; organization: ApiOrganization }>('/api/auth/register', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ logged_out: boolean }>('/api/auth/logout'),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useSwitchOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (organization_id: string) =>
      api.post<{ organization: ApiOrganization }>('/api/app/orgs/switch', { organization_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}
